import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { chiSono, progetti, personale, en } from "@/content";
import { useLang } from "@/lang";
import MacWindow from "@/components/MacWindow";
import PixelIcon from "@/components/PixelIcon";
import Reveal, { EASE, MaskedLine } from "@/components/Reveal";

function ChapterHead({ number, title }) {
    return (
        <div className="md:col-span-4">
            <span className="font-accent-serif text-6xl leading-none text-ink/40 md:text-8xl">
                {number}
            </span>
            <h3 className="mt-3 font-display text-2xl tracking-tight md:text-3xl">
                {title}
            </h3>
        </div>
    );
}

export default function ChiSono() {
    const { lang } = useLang();
    const cs = lang === "en" ? en.chiSono : chiSono;
    const pr = lang === "en" ? en.progetti : progetti;
    const pe = lang === "en" ? en.personale : personale;
    return (
        <MacWindow id="chi-sono" testId="chi-sono-section" title={cs.windowTitle} icon="person">
            <Reveal>
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-ink/60">
                    {cs.overline}
                </p>
            </Reveal>
            <h2 className="mt-5 font-display text-4xl leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl">
                <MaskedLine key={lang}>{cs.heading}</MaskedLine>
            </h2>

            <div className="mt-14 md:mt-20">
                {cs.chapters.map((ch, i) => (
                    <Reveal
                        key={ch.number}
                        delay={0.05 * i}
                        className="grid gap-6 border-t-2 border-ink py-10 md:grid-cols-12 md:py-14"
                    >
                        <ChapterHead number={ch.number} title={ch.title} />
                        <div className="md:col-span-7 md:col-start-6">
                            <p className="max-w-2xl text-base leading-relaxed text-ink/80 md:text-lg">
                                {ch.body}
                            </p>
                        </div>
                    </Reveal>
                ))}

                <Reveal className="grid gap-6 border-t-2 border-ink py-10 md:grid-cols-12 md:py-14">
                    <ChapterHead number="03" title={pr.title} />
                    <div className="md:col-span-7 md:col-start-6">
                        {pr.storia.map((s, i) => (
                            <article
                                key={s.date}
                                data-testid={`storia-item-${i}`}
                                className="border-t-2 border-ink py-6 first:border-t-0 first:pt-0"
                            >
                                <span className="font-accent-serif text-2xl text-ink/70">
                                    {s.date}
                                </span>
                                <h4 className="mt-1 font-display text-xl tracking-tight md:text-2xl">
                                    {s.title}
                                </h4>
                                <p className="mt-2 max-w-lg text-base leading-relaxed text-ink/80">
                                    {s.text}
                                </p>
                                <a
                                    href={s.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`storia-link-${i}`}
                                    className="group mt-4 inline-flex items-center gap-1.5 border-2 border-ink px-3.5 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.12em] transition-colors duration-150 hover:bg-ink hover:text-paper"
                                >
                                    <PixelIcon
                                        name="floppy"
                                        className="h-3.5 w-3.5 transition duration-150 group-hover:invert"
                                    />
                                    {s.linkLabel}
                                    <ArrowUpRight className="h-3 w-3 transition-transform duration-150 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                                </a>
                            </article>
                        ))}
                        <div className="mt-10">
                            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-ink/60">
                                {pr.itemsLabel}
                            </p>
                            <div className="mt-3">
                                {pr.items.map((p, i) => (
                                    <a
                                        key={p.name}
                                        href={p.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`progetto-link-${i}`}
                                        className="group flex items-center justify-between gap-4 border-t-2 border-ink py-5 transition-colors duration-150 last:border-b-2 hover:bg-ink hover:text-paper"
                                    >
                                        <span>
                                            <span className="block font-display text-xl tracking-tight md:text-2xl">
                                                {p.name}
                                            </span>
                                            <span className="mt-1 block text-[11px] font-semibold uppercase tracking-[0.2em] opacity-60">
                                                {p.note}
                                            </span>
                                        </span>
                                        <ArrowUpRight className="h-5 w-5 shrink-0 transition-transform duration-150 group-hover:-translate-y-1 group-hover:translate-x-1" />
                                    </a>
                                ))}
                            </div>
                        </div>
                        <blockquote data-testid="gandhi-quote" className="mt-12 max-w-xl">
                            <p className="font-accent-serif text-3xl leading-snug text-ink/90 md:text-4xl">
                                “{pr.quote.text}”
                            </p>
                            <cite className="mt-4 block text-[11px] font-semibold uppercase not-italic tracking-[0.25em] text-ink/60">
                                {pr.quote.author}
                            </cite>
                        </blockquote>
                    </div>
                </Reveal>

                <Reveal className="border-t-2 border-ink py-10 md:py-14">
                    <div className="grid gap-6 md:grid-cols-12">
                        <ChapterHead number="04" title={pe.title} />
                        <div className="md:col-span-7 md:col-start-6">
                            {pe.date.map((d, i) => (
                                <div
                                    key={d.year}
                                    data-testid={`vita-date-item-${i}`}
                                    className="hover-dither grid grid-cols-12 items-baseline gap-4 border-t-2 border-ink py-5 transition-colors duration-150 first:border-t-0 first:pt-0"
                                >
                                    <span className="col-span-4 font-accent-serif text-2xl text-ink/70 md:col-span-3 md:text-3xl">
                                        {d.year}
                                    </span>
                                    <p className="col-span-8 text-base leading-relaxed text-ink/80 md:col-span-9">
                                        {d.text}
                                    </p>
                                </div>
                            ))}
                            <div className="mt-10 grid gap-6 md:grid-cols-12">
                                <motion.div
                                    data-testid="personale-photo"
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-60px" }}
                                    transition={{ duration: 0.9, ease: EASE }}
                                    className="mac-window self-start md:col-span-5"
                                >
                                    <div className="mac-titlebar">
                                        <span className="mac-closebox" />
                                        <span className="mac-titlebar-title flex items-center gap-2">
                                            <PixelIcon name="photo" className="h-3.5 w-3.5" />
                                            ecco chi sono...
                                        </span>
                                    </div>
                                    <img
                                        src={cs.image.url}
                                        alt={cs.image.alt}
                                        data-testid="chi-sono-image"
                                        className="w-full"
                                    />
                                </motion.div>
                                <div className="border-2 border-ink bg-paper p-7 shadow-[5px_5px_0_#000] md:col-span-7 md:p-8">
                                    <h4 className="text-xs font-semibold uppercase tracking-[0.25em] text-ink/60">
                                        {pe.schedaLabel}
                                    </h4>
                                    <dl className="mt-5 space-y-4">
                                        {pe.scheda.map((s) => (
                                            <div
                                                key={s.label}
                                                data-testid={`scheda-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                                className="border-t-2 border-ink pt-4 first:border-t-0 first:pt-0"
                                            >
                                                <dt className="text-[11px] font-semibold uppercase tracking-[0.2em] text-ink/60">
                                                    {s.label}
                                                </dt>
                                                <dd className="mt-1 font-display text-lg leading-snug tracking-tight">
                                                    {s.value}
                                                </dd>
                                            </div>
                                        ))}
                                    </dl>
                                </div>
                            </div>
                            <p
                                data-testid="motto"
                                className="mt-8 border-l-4 border-ink pl-6 font-accent-serif text-2xl leading-snug text-ink/90 md:text-3xl"
                            >
                                “{pe.motto}”
                            </p>
                        </div>
                    </div>
                    <div className="mt-12 grid gap-10 md:grid-cols-3 md:gap-12">
                        {pe.preferiti.map((p, i) => (
                            <div
                                key={p.title}
                                data-testid={`preferito-${i}`}
                                className="border-t-4 border-ink pt-5"
                            >
                                <h4 className="font-display text-xl tracking-tight md:text-2xl">
                                    {p.title}
                                </h4>
                                <p className="mt-3 text-base leading-relaxed text-ink/80">
                                    {p.text}
                                    {p.links &&
                                        p.links.map((l, j) => (
                                            <span key={l.url}>
                                                {" "}
                                                ·{" "}
                                                <a
                                                    href={l.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    data-testid={`preferito-link-${j}`}
                                                    className="underline decoration-2 underline-offset-4 transition-colors duration-150 hover:bg-ink hover:text-paper hover:no-underline"
                                                >
                                                    {l.label}
                                                </a>
                                            </span>
                                        ))}
                                </p>
                            </div>
                        ))}
                    </div>
                </Reveal>
            </div>
        </MacWindow>
    );
}
